import { serviceValues, type AppliedTaxes } from "../types/budget/service-item";
import { EventScopeEnum } from "../types/project/enums";

/** Ordem das pills em Taxas padrão e na modal Taxas do serviço (sem overhead / imposto fornecedor). */
export const APPLIED_TAX_PILL_KEYS: (keyof AppliedTaxes)[] = [
  "serviceTax",
  "iss",
  "cbs",
  "ibs",
  "tribute",
  "localTax",
  "tourismTax",
  "fee",
  "iof",
  "markup",
  "commission",
  "incentive",
  "doubleCharged",
];

export function normalizeTaxPillCurrency(currency: string | undefined): string {
  const normalized = String(currency ?? "BRL")
    .trim()
    .toUpperCase();
  return normalized || "BRL";
}

/** Moeda efetiva das pills: nacional força BRL (igual effectiveCategoryExchange). */
export function resolveTaxPillCurrency(
  currency: string | undefined,
  eventScope?: string
): string {
  if (eventScope === EventScopeEnum.NATIONAL) {
    return "BRL";
  }
  return normalizeTaxPillCurrency(currency);
}

/**
 * CBS/IBS (Reforma Tributária) ainda não entraram em vigência: as pills ficam
 * ocultas, exatamente como já acontece no bloco Valores (ver o filtro de
 * `typeTaxOptions` em `BaseMonetaryData`). Ocultas, elas também não podem
 * disparar o alerta de "taxas diferentes do padrão" — ver `hasCustomTaxes`.
 * O cálculo e os dados seguem intactos: para liberar, basta ligar esta flag
 * (ou publicar com REACT_APP_ENABLE_CBS_IBS=true).
 */
export const CBS_IBS_PILLS_ENABLED =
  process.env.REACT_APP_ENABLE_CBS_IBS === "true";

/**
 * Mesma regra do bloco Valores ({@link BaseMonetaryData} / cálculo): em BRL não se
 * aplica IOF; em moeda estrangeira não se aplica ISS. CBS/IBS valem em qualquer
 * moeda, mas só aparecem quando a Reforma Tributária estiver em vigência.
 */
export function isIssDefaultTaxPillVisible(currency: string): boolean {
  return resolveTaxPillCurrency(currency) === "BRL";
}

export function isCbsDefaultTaxPillVisible(_currency: string): boolean {
  return CBS_IBS_PILLS_ENABLED;
}

export function isIbsDefaultTaxPillVisible(_currency: string): boolean {
  return CBS_IBS_PILLS_ENABLED;
}

export function isIofDefaultTaxPillVisible(currency: string): boolean {
  return resolveTaxPillCurrency(currency) !== "BRL";
}

/** Alinha flags ISS/IOF à moeda (espelha sanitizeMainAppliedTaxesWithBaseCurrency na API). */
export function sanitizeAppliedTaxesForCurrency(
  appliedTaxes: AppliedTaxes,
  currency: string,
  eventScope?: string
): AppliedTaxes {
  const resolved = resolveTaxPillCurrency(currency, eventScope);
  const next = { ...appliedTaxes };

  if (resolved === "BRL") {
    next.iof = false;
    next.iss = true;
  } else {
    next.iss = false;
    next.iof = true;
  }

  return next;
}

/** Taxas que não entram nas pills (ex.: over no markup; imposto fornecedor no bloco de valores). */
export function isDefaultTaxPillVisible(taxKey: string): boolean {
  return taxKey !== "overhead" && taxKey !== "supplier_tribute";
}

/** Pills de taxas padrão e do serviço (TaxesModal): nacional × internacional alinhado ao bloco Valores. */
export function isAppliedTaxPillVisible(
  taxKey: string,
  currency: string,
  eventScope?: string
): boolean {
  if (!isDefaultTaxPillVisible(taxKey)) return false;
  const resolved = resolveTaxPillCurrency(currency, eventScope);
  if (taxKey === "iss" && !isIssDefaultTaxPillVisible(resolved)) return false;
  if (taxKey === "cbs" && !isCbsDefaultTaxPillVisible(resolved)) return false;
  if (taxKey === "ibs" && !isIbsDefaultTaxPillVisible(resolved)) return false;
  if (taxKey === "iof" && !isIofDefaultTaxPillVisible(resolved)) return false;
  return true;
}

/* ----------------------------------------------------------------------------
 * MMV-2257 (existe só na hml, não foi para a develop): sincronização ISS/IOF do
 * stack de Services a partir de baseValues. Preservado no cherry-pick da
 * MMV-2485 porque `ServicesContext/reducer-function.ts` depende destas funções.
 * -------------------------------------------------------------------------- */

/** Atualiza só iss/iof conforme a moeda (ex.: voltou de USD para BRL → iss true). */
export function patchIssIofAppliedTaxesForCurrency(
  applied: AppliedTaxes,
  currency: string | undefined
): AppliedTaxes {
  const c = currency ?? "BRL";
  if (c === "BRL" || c === "") {
    return { ...applied, iss: true, iof: false };
  }
  return { ...applied, iss: false, iof: true };
}

type BaseValueLike = {
  supplierId: string;
  category: string;
  calcOption: string;
  version?: number;
  exchange?: { currency?: string };
};

type MainAppliedLike = {
  supplierId: string;
  category: string;
  calcOption: string;
  version: number;
  appliedTaxes: AppliedTaxes;
};

export function syncMainAppliedTaxesIssIofFromBaseValues<
  T extends MainAppliedLike
>(rows: T[], baseValues: BaseValueLike[]): T[] {
  return rows.map((row) => {
    const bv =
      baseValues.find(
        (b) =>
          b.supplierId === row.supplierId &&
          b.category === row.category &&
          b.calcOption === row.calcOption &&
          b.version === row.version
      ) ||
      baseValues.find(
        (b) =>
          b.supplierId === row.supplierId &&
          b.category === row.category &&
          b.calcOption === row.calcOption
      );
    const ccy = bv?.exchange?.currency;
    return {
      ...row,
      appliedTaxes: patchIssIofAppliedTaxesForCurrency(row.appliedTaxes, ccy),
    };
  });
}

export function syncBudgetServicesAppliedTaxesIssIofFromBaseValues(
  services: ServiceDataLike[],
  baseValues: BaseValueLike[]
): ServiceDataLike[] {
  const defaultApplied = serviceValues.appliedTaxes;
  return (services || []).map((svc) => {
    const bv = baseValues.find(
      (b) =>
        b.supplierId === svc.supplierId &&
        b.category === svc.type &&
        b.calcOption === svc.calcOption
    );
    const ccy = bv?.exchange?.currency;
    const patch = (a: AppliedTaxes | undefined) =>
      patchIssIofAppliedTaxesForCurrency({ ...defaultApplied, ...a }, ccy);
    const next: ServiceDataLike = {
      ...svc,
      budgetValues: {
        ...svc.budgetValues,
        appliedTaxes: patch(svc.budgetValues?.appliedTaxes),
      },
    };
    if (Array.isArray(svc.negotiationValue) && svc.negotiationValue.length) {
      next.negotiationValue = svc.negotiationValue.map(
        (nv: NegotiationSliceLike) => ({
          ...nv,
          appliedTaxes: patch(
            nv.appliedTaxes ?? svc.budgetValues?.appliedTaxes
          ),
        })
      );
    }
    return next;
  });
}

type NegotiationSliceLike = { appliedTaxes?: AppliedTaxes };

type ServiceDataLike = {
  supplierId: string;
  type: string;
  calcOption: string;
  budgetValues?: { appliedTaxes?: AppliedTaxes };
  negotiationValue?: NegotiationSliceLike[];
};
